<script lang="ts">
/** One thing the user has to tick before the action is allowed. */
export interface GaiaAckItem {
  key: string;
  text: string;
  checked: boolean;
}
</script>

<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaBanner from './GaiaBanner.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaNeutral } from '../themes/tokens';
import GaiaCheckbox from '../base/GaiaCheckbox.vue';

const props = defineProps({
  items: {
    type: Array as PropType<GaiaAckItem[]>,
    required: true,
  },
  /** Banner headline, e.g. "Existing signatures will be cleared". */
  title: {
    type: String,
    default: undefined,
  },
  /** Banner tone — error for destructive consequences, warning for reversible ones. */
  type: {
    type: String as PropType<'error' | 'warning' | 'info' | 'default'>,
    default: 'error',
  },
  /** Locks every row while the action it gates is running. */
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ toggle: [key: string, checked: boolean] }>();

const rowBorder = gaiaNeutral.borderSubtle;
const surface = gaiaNeutral.surface;
const hover = gaiaNeutral.surfaceHover;
</script>

<template>
  <!-- A consequence the user must acknowledge before a destructive action is
       allowed: the explanation in a tinted banner, then one tick per thing at
       stake (a file whose signature will be cleared, a caveat on a download).
       The gate itself is the caller's — it decides what "all ticked" unlocks. -->
  <gaia-banner :type="props.type" :title="props.title">
    <gaia-text v-if="$slots.default" size="dense" :style="{ lineHeight: 1.6 }">
      <slot />
    </gaia-text>
    <n-flex vertical :size="6" :style="{ marginTop: '10px' }">
      <label
        v-for="item in props.items"
        :key="item.key"
        class="row"
        :class="{ disabled: props.disabled }"
      >
        <gaia-checkbox
          :checked="item.checked"
          :disabled="props.disabled"
          @update:checked="(checked: boolean) => emit('toggle', item.key, checked)"
        />
        <gaia-text size="dense" tone="text" :style="{ lineHeight: 1.55 }">
          {{ item.text }}
        </gaia-text>
      </label>
    </n-flex>
  </gaia-banner>
</template>

<style scoped>
/* Each acknowledgement is its own hit target on the banner's tint, so a row of
   long copy stays clickable end to end rather than only on the box. */
.row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 9px 11px;
  border: 1px solid v-bind(rowBorder);
  border-radius: 9px;
  background: v-bind(surface);
  cursor: pointer;
}

.row:hover {
  background: v-bind(hover);
}

.row.disabled {
  cursor: default;
}
</style>
